<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import { useAccount } from 'dashboard/composables/useAccount';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import FlowSelect from './FlowSelect.vue';
import FlowMetaEventSelect from './FlowMetaEventSelect.vue';
import MetaConversionModal from './MetaConversionModal.vue';
import { findField, decodeField } from './conditionFields';
import { isMetaAction, metaMappedCount } from './metaConversion';
import {
  PIXEL_DATA,
  PIXEL_DESTINATIONS,
  PIXEL_EVENT_TYPES,
  WAIT_UNITS,
  getRecordFieldGroups,
  isPixelAction,
  isWaitAction,
  newFieldRow,
} from './actionCatalog';
import { FIELD_HEIGHT, STAGES_BOX_HEIGHT } from './flowHelper';
import { useMetaPixel } from './useMetaPixel';

const props = defineProps({
  data: { type: Object, required: true },
  errors: { type: Array, default: () => [] },
  fieldGroups: { type: Array, default: () => [] },
});

const emit = defineEmits(['update']);

const { t } = useI18n();
const pipelinesStore = usePipelinesStore();
const { accountScopedUrl } = useAccount();
const { connected: metaConnected, label: metaPixelLabel } = useMetaPixel();

const INPUT_CLASS =
  '!mb-0 !h-8 w-full !px-2 !py-0 text-sm rounded-lg border bg-n-solid-1 text-n-slate-12 outline-none focus:border-n-brand';

const rowStyle = { height: `${FIELD_HEIGHT}px` };
const boxStyle = { height: `${STAGES_BOX_HEIGHT}px` };

const invalid = (key, index = null) =>
  props.errors.includes(index === null ? key : `${key}@${index}`);

const invalidAny = (keys, index = null) =>
  keys.some(key => invalid(key, index));

const patch = changes => emit('update', { ...props.data, ...changes });

const isPixel = computed(() => isPixelAction(props.data.action));
const isMeta = computed(() => isMetaAction(props.data.action));
const isWait = computed(() => isWaitAction(props.data.action));
const showMetaModal = ref(false);

const applyMeta = changes => {
  patch(changes);
  showMetaModal.value = false;
};
const rows = computed(() => props.data.fields || []);
const recordGroups = computed(() =>
  getRecordFieldGroups(props.fieldGroups, props.data.action)
);

const pipelines = computed(() =>
  [...pipelinesStore.getPipelinesList].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0)
  )
);
const stages = computed(
  () =>
    pipelines.value.find(({ id }) => id === props.data.pipeline_id)?.stages ||
    []
);

const rowField = row =>
  row.field ? findField(recordGroups.value, decodeField(row.field)) : null;

const patchRow = (index, changes) =>
  patch({
    fields: rows.value.map((row, i) =>
      i === index ? { ...row, ...changes } : row
    ),
  });

const fieldLabel = field => field.label || t(field.labelKey);

const valueOptions = field => {
  if (field.type === 'boolean') {
    return [
      { value: 'true', label: t('AUTOMATION.FLOW.YES') },
      { value: 'false', label: t('AUTOMATION.FLOW.NO') },
    ];
  }
  return field.options?.length ? field.options : null;
};

// Com uma variável ({{ deal.value }}) no valor, o campo vira texto para exibi-la.
const hasVariable = value => String(value ?? '').includes('{{');

const inputType = (field, value) =>
  hasVariable(value) ? 'text' : { number: 'number', date: 'date' }[field?.type];

// Variáveis do evento: cada campo de Contato, Empresa e Negociação vira {{ origem.campo }}.
const variableGroups = computed(() =>
  props.fieldGroups.map(group => ({
    id: group.id,
    label: t(group.labelKey),
    variables: group.fields.map(field => ({
      label: fieldLabel(field),
      token: `{{ ${field.source}.${field.scope === 'custom' ? 'custom.' : ''}${field.attribute} }}`,
    })),
  }))
);

const insertVariable = (index, event) => {
  const token = event.target.value;
  event.target.value = '';
  if (!token) return;
  const current = String(rows.value[index].value ?? '');
  patchRow(index, { value: current ? `${current} ${token}` : token });
};

const toggleIn = (key, value) => {
  const list = props.data[key] || [];
  patch({
    [key]: list.includes(value)
      ? list.filter(item => item !== value)
      : [...list, value],
  });
};

const setPipeline = value =>
  patch({ pipeline_id: value ? Number(value) : '', stage_id: '' });
</script>

<template>
  <div v-if="isWait" class="flex items-center gap-1" :style="rowStyle">
    <input
      :class="[
        INPUT_CLASS,
        invalidAny(['WAIT_AMOUNT_REQUIRED', 'WAIT_TOO_LONG'])
          ? 'border-n-ruby-9'
          : 'border-n-weak',
      ]"
      type="number"
      min="1"
      step="1"
      :value="data.amount"
      :placeholder="t('AUTOMATION.FLOW.WAIT.AMOUNT_PLACEHOLDER')"
      @input="patch({ amount: $event.target.value })"
    />
    <FlowSelect
      :invalid="invalid('WAIT_TOO_LONG')"
      :model-value="data.unit || 'minutes'"
      @update:model-value="patch({ unit: $event })"
    >
      <option v-for="unit in WAIT_UNITS" :key="unit" :value="unit">
        {{ t(`AUTOMATION.FLOW.WAIT.UNITS.${unit.toUpperCase()}`) }}
      </option>
    </FlowSelect>
  </div>

  <template v-else-if="isMeta">
    <div
      class="flex items-center gap-2 px-2 text-xs rounded-lg border"
      :class="
        metaConnected
          ? 'border-n-weak text-n-slate-11'
          : invalid('META_NOT_CONNECTED')
            ? 'border-n-ruby-9 text-n-ruby-11'
            : 'border-n-amber-7 text-n-amber-11'
      "
      :style="rowStyle"
    >
      <Icon icon="i-lucide-target" class="size-3.5 shrink-0" />
      <span v-if="metaConnected" class="truncate">
        {{ t('AUTOMATION.FLOW.META.PIXEL', { name: metaPixelLabel }) }}
      </span>
      <template v-else>
        <span class="truncate">
          {{ t('AUTOMATION.FLOW.META.NOT_CONNECTED') }}
        </span>
        <a
          :href="accountScopedUrl('settings/integrations/meta_ads')"
          target="_blank"
          rel="noopener noreferrer"
          class="ml-auto font-medium underline shrink-0"
        >
          {{ t('AUTOMATION.FLOW.META.CONNECT') }}
        </a>
      </template>
    </div>
    <FlowMetaEventSelect
      :has-error="invalid('META_EVENT_REQUIRED')"
      :model-value="data.meta_event"
      @update:model-value="$event && patch({ meta_event: $event })"
    />
    <button
      type="button"
      class="flex items-center justify-center gap-1 !p-0 text-xs font-medium rounded-lg border text-n-slate-11 hover:bg-n-alpha-2"
      :class="
        invalidAny([
          'META_IDENTITY_REQUIRED',
          'META_CHANNEL_REQUIRED',
          'META_MESSAGING_IDENTITY_REQUIRED',
          'META_FIELD_MISSING',
        ])
          ? 'border-n-ruby-9'
          : 'border-n-weak'
      "
      :style="rowStyle"
      @click="showMetaModal = true"
    >
      <Icon icon="i-lucide-settings-2" class="size-3.5" />
      {{
        t('AUTOMATION.FLOW.META.CONFIGURE_FIELDS', {
          count: metaMappedCount(data),
        })
      }}
    </button>
    <MetaConversionModal
      v-if="showMetaModal"
      :data="data"
      :field-groups="fieldGroups"
      :errors="errors"
      @close="showMetaModal = false"
      @apply="applyMeta"
    />
  </template>

  <template v-else-if="isPixel">
    <div
      class="flex items-center gap-4 px-2 rounded-lg border"
      :class="
        invalid('DESTINATION_REQUIRED') ? 'border-n-ruby-9' : 'border-n-weak'
      "
      :style="rowStyle"
    >
      <label
        v-for="destination in PIXEL_DESTINATIONS"
        :key="destination"
        class="flex items-center gap-2 !mb-0 text-sm cursor-pointer text-n-slate-12"
      >
        <input
          type="checkbox"
          class="!mb-0 !size-4"
          :checked="(data.destinations || []).includes(destination)"
          @change="toggleIn('destinations', destination)"
        />
        {{ t(`AUTOMATION.FLOW.PIXEL.${destination.toUpperCase()}`) }}
      </label>
    </div>
    <FlowSelect
      :invalid="invalid('EVENT_TYPE_REQUIRED')"
      :model-value="data.event_type"
      @update:model-value="patch({ event_type: $event })"
    >
      <option value="" disabled>
        {{ t('AUTOMATION.FLOW.PIXEL.EVENT_PLACEHOLDER') }}
      </option>
      <option v-for="type in PIXEL_EVENT_TYPES" :key="type" :value="type">
        {{ t(`AUTOMATION.FLOW.PIXEL.EVENTS.${type.toUpperCase()}`) }}
      </option>
    </FlowSelect>
    <div
      class="grid grid-cols-2 content-start gap-x-2 px-2 overflow-y-auto rounded-lg border border-n-weak"
      :style="boxStyle"
    >
      <label
        v-for="item in PIXEL_DATA"
        :key="item"
        class="flex items-center gap-2 !mb-0 text-sm cursor-pointer text-n-slate-12"
        :style="rowStyle"
      >
        <input
          type="checkbox"
          class="!mb-0 !size-4"
          :checked="(data.include || []).includes(item)"
          @change="toggleIn('include', item)"
        />
        <span class="truncate">
          {{ t(`AUTOMATION.FLOW.PIXEL.DATA.${item.toUpperCase()}`) }}
        </span>
      </label>
    </div>
  </template>

  <template v-else>
    <template v-if="data.action === 'create_deal'">
      <FlowSelect
        :invalid="invalidAny(['PIPELINE_REQUIRED', 'PIPELINE_MISSING'])"
        :model-value="String(data.pipeline_id || '')"
        @update:model-value="setPipeline"
      >
        <option value="" disabled>
          {{ t('AUTOMATION.FLOW.PIPELINE_PLACEHOLDER') }}
        </option>
        <option
          v-for="pipeline in pipelines"
          :key="pipeline.id"
          :value="String(pipeline.id)"
        >
          {{ pipeline.name }}
        </option>
      </FlowSelect>
      <FlowSelect
        :invalid="invalidAny(['STAGE_REQUIRED', 'STAGE_MISSING'])"
        :model-value="String(data.stage_id || '')"
        @update:model-value="patch({ stage_id: Number($event) })"
      >
        <option value="" disabled>
          {{ t('AUTOMATION.FLOW.STAGE_PLACEHOLDER') }}
        </option>
        <option
          v-for="stage in stages"
          :key="stage.id"
          :value="String(stage.id)"
        >
          {{ stage.name }}
        </option>
      </FlowSelect>
    </template>

    <template v-for="(row, index) in rows" :key="index">
      <div class="flex items-center gap-1" :style="rowStyle">
        <FlowSelect
          :invalid="invalidAny(['FIELD_REQUIRED', 'FIELD_MISSING'], index)"
          :model-value="row.field"
          @update:model-value="
            patchRow(index, {
              field: $event,
              value: '',
            })
          "
        >
          <option value="" disabled>
            {{ t('AUTOMATION.FLOW.SELECT_ATTRIBUTE') }}
          </option>
          <optgroup
            v-for="group in recordGroups"
            :key="group.id"
            :label="t(group.labelKey)"
          >
            <option
              v-for="field in group.fields"
              :key="field.id"
              :value="field.id"
            >
              {{ fieldLabel(field) }}
            </option>
          </optgroup>
        </FlowSelect>
        <button
          v-if="rows.length > 1"
          type="button"
          class="grid place-items-center shrink-0 !p-0 size-6 rounded hover:bg-n-alpha-2 text-n-slate-11"
          :title="t('AUTOMATION.FLOW.REMOVE_FIELD')"
          @click="patch({ fields: rows.filter((_, i) => i !== index) })"
        >
          <Icon icon="i-lucide-trash-2" class="size-3.5" />
        </button>
      </div>
      <ComboBox
        v-if="rowField(row) && valueOptions(rowField(row))"
        :has-error="
          invalidAny(
            [
              'FIELD_VALUE_REQUIRED',
              'FIELD_VALUE_MISSING',
              'FIELD_VALUE_INVALID',
            ],
            index
          )
        "
        :model-value="String(row.value)"
        :options="valueOptions(rowField(row))"
        :placeholder="t('AUTOMATION.FLOW.VALUE')"
        @update:model-value="patchRow(index, { value: $event })"
      />
      <div v-else class="relative">
        <input
          class="!pr-8"
          :class="[
            INPUT_CLASS,
            invalidAny(
              [
                'FIELD_VALUE_REQUIRED',
                'FIELD_VALUE_MISSING',
                'FIELD_VALUE_INVALID',
              ],
              index
            )
              ? 'border-n-ruby-9'
              : 'border-n-weak',
          ]"
          :style="rowStyle"
          :type="inputType(rowField(row), row.value) || 'text'"
          :value="row.value"
          :placeholder="t('AUTOMATION.FLOW.VALUE')"
          @input="patchRow(index, { value: $event.target.value })"
        />
        <label
          class="absolute grid -translate-y-1/2 rounded cursor-pointer right-1 top-1/2 place-items-center size-6 text-n-slate-11 hover:bg-n-alpha-2"
          :title="t('AUTOMATION.FLOW.INSERT_VARIABLE')"
        >
          <Icon icon="i-lucide-braces" class="size-3.5" />
          <select
            class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            :aria-label="t('AUTOMATION.FLOW.INSERT_VARIABLE')"
            @change="insertVariable(index, $event)"
          >
            <option value="" disabled selected>
              {{ t('AUTOMATION.FLOW.INSERT_VARIABLE') }}
            </option>
            <optgroup
              v-for="group in variableGroups"
              :key="group.id"
              :label="group.label"
            >
              <option
                v-for="variable in group.variables"
                :key="variable.token"
                :value="variable.token"
              >
                {{ variable.label }}
              </option>
            </optgroup>
          </select>
        </label>
      </div>
    </template>

    <button
      type="button"
      class="flex items-center justify-center gap-1 !p-0 text-xs font-medium rounded-lg border border-dashed text-n-slate-11 hover:bg-n-alpha-2"
      :class="invalid('FIELDS_REQUIRED') ? 'border-n-ruby-9' : 'border-n-weak'"
      :style="rowStyle"
      @click="patch({ fields: [...rows, newFieldRow()] })"
    >
      <Icon icon="i-lucide-plus" class="size-3.5" />
      {{ t('AUTOMATION.FLOW.ADD_FIELD') }}
    </button>
  </template>
</template>
